import React, { useState, useEffect, useRef } from "react";
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { useAuthContext } from "../../context/AuthContext";
import { API } from "../../constant";
import { setToken, getToken } from "../../helpers";
import { Link, useNavigate } from "react-router-dom";
import Loading from "../Hooks/Loading";
import logo2 from "../../Media/LogoReverseWithTag.svg";
import { loadCaptchaEnginge, LoadCanvasTemplate, validateCaptcha } from 'react-simple-captcha';

const COUNTRIES = [
    { code: 'US', name: 'United States' }, { code: 'GB', name: 'United Kingdom' },
    { code: 'CA', name: 'Canada' }, { code: 'AU', name: 'Australia' },
    { code: 'DE', name: 'Germany' }, { code: 'FR', name: 'France' },
    { code: 'IN', name: 'India' }, { code: 'PK', name: 'Pakistan' },
    { code: 'AE', name: 'United Arab Emirates' }, { code: 'SA', name: 'Saudi Arabia' },
    { code: 'SG', name: 'Singapore' }, { code: 'NZ', name: 'New Zealand' },
    { code: 'IE', name: 'Ireland' }, { code: 'NL', name: 'Netherlands' },
    { code: 'SE', name: 'Sweden' }, { code: 'NO', name: 'Norway' },
    { code: 'DK', name: 'Denmark' }, { code: 'CH', name: 'Switzerland' },
    { code: 'ES', name: 'Spain' }, { code: 'IT', name: 'Italy' },
    { code: 'PT', name: 'Portugal' }, { code: 'BE', name: 'Belgium' },
    { code: 'AT', name: 'Austria' }, { code: 'PL', name: 'Poland' },
    { code: 'BR', name: 'Brazil' }, { code: 'MX', name: 'Mexico' },
    { code: 'ZA', name: 'South Africa' }, { code: 'JP', name: 'Japan' },
    { code: 'CN', name: 'China' }, { code: 'KR', name: 'South Korea' },
    { code: 'MY', name: 'Malaysia' }, { code: 'PH', name: 'Philippines' },
    { code: 'BD', name: 'Bangladesh' }, { code: 'EG', name: 'Egypt' },
    { code: 'TR', name: 'Turkey' }, { code: 'ID', name: 'Indonesia' },
    { code: 'NG', name: 'Nigeria' }, { code: 'KE', name: 'Kenya' },
    { code: 'GH', name: 'Ghana' },
];

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: { fontFamily: '"Roboto", sans-serif', fontSize: '14px', color: '#fff', '::placeholder': { color: 'rgba(255,255,255,0.5)' } },
        invalid: { color: '#fa755a' },
    },
};

const EyeIcon = ({ visible, blinking }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round"
        style={{ animation: blinking ? 'eyeBlink 0.6s ease infinite' : 'none', display: 'block' }}>
        {visible ? (
            <><path d="M1 12S5 5 12 5s11 7 11 7-4 7-11 7S1 12 1 12z" /><circle cx="12" cy="12" r="3" /></>
        ) : (
            <><path d="M17.94 17.94A10.94 10.94 0 0 1 12 19C5 19 1 12 1 12a18.1 18.1 0 0 1 5.06-5.94" />
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
            <line x1="1" y1="1" x2="23" y2="23" /></>
        )}
    </svg>
);

const PasswordField = ({ name, placeholder, required }) => {
    const [visible, setVisible] = useState(false);
    const [blinking, setBlinking] = useState(false);
    const blinkTimer = useRef(null);
    const handleChange = () => {
        setBlinking(true);
        clearTimeout(blinkTimer.current);
        blinkTimer.current = setTimeout(() => setBlinking(false), 1200);
    };
    return (
        <label className="password input-field fade-in-down delay-3" style={{ position: 'relative' }}>
            <input className="field-with-icon" type={visible ? 'text' : 'password'}
                name={name} placeholder={placeholder} required={required} onChange={handleChange} />
            <button type="button" onClick={() => setVisible(v => !v)} className="eye-toggle-btn"
                tabIndex={-1} aria-label={visible ? 'Hide password' : 'Show password'}>
                <EyeIcon visible={visible} blinking={blinking && !visible} />
            </button>
        </label>
    );
};

const SignUpForm = () => {
    const stripe = useStripe();
    const elements = useElements();
    const navigate = useNavigate();
    const { user: authUser, setUser } = useAuthContext();

    const [step, setStep] = useState(1);
    const [step1Values, setStep1Values] = useState({});
    const [address, setAddress] = useState({ line1: '', city: '', state: '', postal_code: '', country: 'US' });
    const [email, setEmail] = useState('');
    const [userCaptcha, setUserCaptcha] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [trialDays, setTrialDays] = useState(null);
    const [plan, setPlan] = useState(null);


    useEffect(() => {
        loadCaptchaEnginge(6, 'transparent', '#fff');
        fetch(`${API}/plans/9`)
            .then(r => r.json())
            .then(d => {
                const attrs = d?.data?.attributes || d?.attributes || d;
                setPlan(attrs);
                const days = attrs?.trialDays;
                setTrialDays(days && days > 0 ? days : null);
            })
            .catch(() => {});
    }, []);

    const goToStep2 = (e) => {
        e.preventDefault();
        setError('');

        const formData = new FormData(e.target.closest('form'));
        const values = {};
        formData.forEach((v, k) => values[k] = v);

        if (values.password !== values.confirmPassword) {
            setError('Passwords do not match!');
            return;
        }
        setStep1Values(values);
        setStep(2);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!validateCaptcha(userCaptcha)) {
            alert('Captcha Does Not Match');
            loadCaptchaEnginge(6, 'transparent', '#fff');
            setUserCaptcha('');
            return;
        }

        if (!stripe || !elements) {
            setError('Payment system not ready. Please refresh and try again.');
            return;
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
            setError('Card field not ready. Please refresh and try again.');
            return;
        }

        try {
            setLoading(true);

            const fullName = `${step1Values.firstName || ''} ${step1Values.lastName || ''}`.trim();

            // Only pass fields Stripe accepts — no undefined/extra React state keys
            const stripeAddress = {
                line1: address.line1 || '',
                city: address.city || '',
                state: address.state || '',
                postal_code: address.postal_code || '',
                country: address.country || 'US',
            };
            const billingDetails = { name: fullName, email: step1Values.email, address: stripeAddress };

            // Validate card FIRST — if this fails the user is never registered
            const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: billingDetails,
            });
            if (pmError) throw new Error(pmError.message);
            const pmId = paymentMethod.id;

            // If the user already registered on a previous attempt (registration succeeded
            // but card failed), their token is still in storage — reuse it, skip re-registration
            let jwt, regUser;
            const existingToken = getToken();

            if (existingToken && authUser) {
                jwt = existingToken;
                regUser = authUser;
            } else {
                const regRes = await fetch(`${API}/auth/local/register`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(step1Values),
                });
                const regData = await regRes.json();
                if (regData?.error) throw regData.error;
                jwt = regData.jwt;
                regUser = regData.user;
                setToken(jwt);
                setUser(regUser);
            }

            if (trialDays) {
                // Trial: SetupIntent → confirmCardSetup using the already-validated PM ID
                const siRes = await fetch(`${API}/stripe/setup-intent`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
                    body: JSON.stringify({
                        customerIds: regUser.customerId || null,
                        customerName: fullName,
                        customerEmail: regUser.email,
                        customerAddress: stripeAddress,
                    }),
                });
                const siData = await siRes.json();
                if (!siRes.ok) throw new Error(siData?.error?.message || 'Failed to initialize payment setup.');

                // Use PM ID (not card element) — card already validated above
                const { setupIntent, error: setupError } = await stripe.confirmCardSetup(siData.clientSecret, {
                    payment_method: pmId,
                });
                if (setupError) throw new Error(setupError.message);

                const subRes = await fetch(`${API}/stripe/create-subscription`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${jwt}`,
                        },
                    body: JSON.stringify({
                        planId: plan?.stripePlanId,
                        paymentMethodId: setupIntent.payment_method,
                        userId: regUser.id,
                        customerId: siData.customerId,
                        customerEmail: regUser.email,
                        customerName: fullName,
                        customerAddress: stripeAddress,
                    }),
                });
                const subData = await subRes.json();
                if (subData?.error) throw new Error(subData.error.message || 'Failed to create trial subscription.');
                if (subData?.subscriptionRecordSaved === false && regUser?.id) {
                    const nextPaymentDate = new Date();
                    nextPaymentDate.setDate(nextPaymentDate.getDate() + Number(trialDays || 30));
                    await fetch(`${API}/subscriptions`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
                        body: JSON.stringify({
                            data: {
                                userId: regUser.id,
                                subscriptionId: subData.subscriptionId,
                                stripeCustomerId: subData.customerId,
                                stripePlanId: plan?.stripePlanId,
                                status: subData.status || 'trialing',
                                startDate: new Date().toISOString(),
                                nextPaymentDate: nextPaymentDate.toISOString(),
                            },
                        }),
                    });
                }

                try {
                    await fetch(`${API}/users/${regUser.id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
                        body: JSON.stringify({
                            customerId: siData.customerId,
                            stripePaymentMethodId: setupIntent.payment_method,
                        }),
                    });
                } catch (_) {}

            } else {
                // No trial: PM already created above, pass directly to subscription
                const subRes = await fetch(`${API}/stripe/create-subscription`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
                    body: JSON.stringify({
                        planId: plan?.stripePlanId,
                        paymentMethodId: pmId,
                        userId: regUser.id,
                        customerEmail: regUser.email,
                        customerName: fullName,
                        customerAddress: stripeAddress,
                    }),
                });
                const subData = await subRes.json();
                if (subData.error) throw new Error(subData.error.message);
                if (subData?.subscriptionRecordSaved === false && regUser?.id) {
                    const nextPaymentDate = new Date();
                    nextPaymentDate.setMonth(nextPaymentDate.getMonth() + 1);
                    await fetch(`${API}/subscriptions`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
                        body: JSON.stringify({
                            data: {
                                userId: regUser.id,
                                subscriptionId: subData.subscriptionId,
                                stripeCustomerId: subData.customerId,
                                stripePlanId: plan?.stripePlanId,
                                status: subData.status || 'active',
                                startDate: new Date().toISOString(),
                                nextPaymentDate: nextPaymentDate.toISOString(),
                            },
                        }),
                    });
                }

                try {
                    await fetch(`${API}/users/${regUser.id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
                        body: JSON.stringify({
                            customerId: subData.customerId,
                            stripePaymentMethodId: paymentMethod.id,
                        }),
                    });
                } catch (_) {}
            }

            // Welcome email — awaited so the request isn't cancelled by navigation
            try {
                await fetch(`${API}/email/user-registration`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        userId: regUser.id,
                        email: regUser.email,
                        username: regUser.username,
                    }),
                });
            } catch (_) {}

            navigate('/intro');

        } catch (err) {
            console.error(err);
            setError(err?.message ?? 'Something went wrong!');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-register login-area-inner">
            {loading && <Loading />}

            <div style={{ display: loading ? 'none' : 'block', width: '100%' }}>
                <div className="login-register-head mb---30 fade-in-down">
                    <img src={logo2} alt="login-register" />
                    <div className="reg-steps">
                        <span className={`reg-step ${step >= 1 ? (step > 1 ? 'done' : 'active') : ''}`}>1</span>
                        <span className="reg-step-line" />
                        <span className={`reg-step ${step === 2 ? 'active' : ''}`}>2</span>
                    </div>
                </div>

                <div className="theme-form">
                    {/* Single form — CardElement stays mounted throughout both steps */}
                    <form className="register-form" onSubmit={step === 1 ? goToStep2 : handleSubmit}>
                        <input hidden type="text" name="username" value={email} readOnly onChange={() => {}} />

                        {/* ── Step 1: Personal details ── */}
                        <div style={{ display: step === 1 ? 'block' : 'none' }}>
                            <label className="user input-field fade-in-down delay-1">
                                <input className="field-with-icon" type="text" name="firstName" placeholder="First Name" required={step === 1} />
                            </label>
                            <label className="user input-field fade-in-down delay-2">
                                <input className="field-with-icon" type="text" name="lastName" placeholder="Last Name" />
                            </label>
                            <label className="email input-field fade-in-down delay-3">
                                <input className="field-with-icon" type="email" name="email" placeholder="Email Address"
                                    required={step === 1} onChange={(e) => setEmail(e.target.value)} />
                            </label>
                            <PasswordField name="password" placeholder="Password" required />
                            <PasswordField name="confirmPassword" placeholder="Confirm Password" required />
                        </div>

                        {/* ── Step 2: Address + Card + Captcha + Checkboxes ── */}
                        <div style={{ display: step === 2 ? 'block' : 'none' }}>
                            <label className="input-field fade-in-down delay-1">
                                <input className="field-with-icon" type="text" placeholder="Address Line 1"
                                    value={address.line1} required={step === 2}
                                    onChange={(e) => setAddress({ ...address, line1: e.target.value })} />
                            </label>
                            <label className="input-field fade-in-down delay-2">
                                <input className="field-with-icon" type="text" placeholder="City"
                                    value={address.city} required={step === 2}
                                    onChange={(e) => setAddress({ ...address, city: e.target.value })} />
                            </label>
                            <label className="input-field fade-in-down delay-2">
                                <input className="field-with-icon" type="text" placeholder="State / Province"
                                    value={address.state} required={step === 2}
                                    onChange={(e) => setAddress({ ...address, state: e.target.value })} />
                            </label>
                            <label className="input-field fade-in-down delay-3">
                                <input className="field-with-icon" type="text" placeholder="Zip / Postal Code"
                                    value={address.postal_code} required={step === 2}
                                    onChange={(e) => setAddress({ ...address, postal_code: e.target.value })} />
                            </label>
                            <div className="register-form fade-in-down delay-3" style={{ padding: 0 }}>
                                <select value={address.country} onChange={(e) => setAddress({ ...address, country: e.target.value })} required={step === 2}>
                                    {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                                </select>
                            </div>

                            <div className="payment-details fade-in-down delay-4">
                                <p className="heading">
                                    Card Details
                                    <span style={{ fontWeight: 300, fontSize: '12px', opacity: 0.8 }}>
                                        {trialDays ? ` — ${trialDays}-day free trial, cancel anytime` : ' — you will be charged immediately'}
                                    </span>
                                </p>
                                <CardElement options={CARD_ELEMENT_OPTIONS} />
                            </div>

                            <span className="captcha login-captcha fade-in-down delay-5">
                                <LoadCanvasTemplate />
                                <label className="captcha-feld">
                                    <input placeholder="Enter Captcha Value" id="user_captcha_input"
                                        name="user_captcha_input" type="text"
                                        value={userCaptcha} onChange={(e) => setUserCaptcha(e.target.value)} />
                                </label>
                            </span>

                            <div className="check-wrap mt---20 fade-in-down delay-5">
                                <div className="checkbox">
                                    <input type="checkbox" name="acceptTerms" required={step === 2} />
                                    <div className="btn-white">I accept Cope Ahead <Link to="/terms-and-conditions">Terms & Conditions</Link></div>
                                </div>
                                <div className="checkbox">
                                    <input type="checkbox" name="understandTherapy" required={step === 2} />
                                    I understand Cope Ahead is not a replacement for therapy
                                </div>
                            </div>
                        </div>

                        {error && <div className="error-message">{error}</div>}

                        {/* Step 1: Next button */}
                        {step === 1 && (
                            <button className="btn btn-gradient btn-full btn-white mt---20 bounce-hover" type="submit">
                                Next
                            </button>
                        )}

                        {/* Step 2: Back + Submit */}
                        {step === 2 && (
                            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                                <button type="button" onClick={() => { setStep(1); setError(''); }}
                                    className="btn btn-gradient btn-white"
                                    style={{ flex: '0 0 auto', padding: '16px 20px', borderRadius: '50px', fontSize: '14px', fontWeight: 300, border: 'none', cursor: 'pointer' }}>
                                    ← Back
                                </button>
                                <button className="btn btn-gradient btn-full btn-white bounce-hover" type="submit"
                                    disabled={loading || !stripe} style={{ flex: 1 }}>
                                    {trialDays ? 'Start Free Trial' : 'Register & Pay'}
                                </button>
                            </div>
                        )}
                    </form>

                    <div className="btn btn-link btn-white ctr mt---20">
                        <p className="hover-bounce mb---10"><Link to="/Login">Login Here</Link></p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignUpForm;
